<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  import { gsap } from 'gsap/dist/gsap';
  import { ScrollTrigger } from 'gsap/dist/ScrollTrigger';
  import { resizeObserver } from '$lib/helpers/browser/resize-observer';

  export let expandingElementSelector: string;
  export let useOnMobile: boolean = false;
  let expandingElement: HTMLElement;
  let timeline: any;
  let unobserveResizeObserver: ReturnType<typeof resizeObserver>;
  let mm;
  const breakpointForMobile = 1024; // medium breakpoint

  gsap.registerPlugin(ScrollTrigger);

  const initExpandScrollTrigger = () => {
    const defaultValues = {
      '--scrollPercent': '0',
    };

    const expandedValues = {
      '--scrollPercent': '100',
    };

    mm = gsap.matchMedia();
    mm.add(
      {
        isDesktop: `(min-width: ${breakpointForMobile}px)`,
        isMobile: `(max-width: ${breakpointForMobile - 1}px)`,
      },
      (context) => {
        if (context?.conditions) {
          // Ensure conditions exist before destructuring
          const { isDesktop, isMobile } = context.conditions;

          if ((isMobile && useOnMobile) || isDesktop) {
            timeline = gsap
              .timeline({
                defaults: { ease: 'power2.in' },
                scrollTrigger: {
                  trigger: expandingElement,
                  scrub: true,
                  start: 'top 80%',
                  end: () => `${expandingElement.offsetHeight} 20%`,
                  invalidateOnRefresh: true,
                  // markers: true,
                },
              })
              .set(expandingElement, { ...defaultValues })
              .to(expandingElement, expandedValues)
              .to(expandingElement, { ...defaultValues, immediateRender: false }, '+=1');
          }
        }
      }
    );
  };

  const onHeightChange = () => {
    if (timeline?.scrollTrigger) {
      timeline.scrollTrigger.refresh();
    }
  };

  onMount(() => {
    expandingElement = document.querySelector(expandingElementSelector) as HTMLElement;

    initExpandScrollTrigger();
    expandingElement.classList.add('expanding');
    expandingElement.classList.remove('onLoad');
    const basePageElement = document.querySelector('.BasePage');
    if (basePageElement) {
      unobserveResizeObserver = resizeObserver(basePageElement, () => {
        onHeightChange();
      });
    }
  });

  onDestroy(() => {
    if (unobserveResizeObserver) {
      unobserveResizeObserver();
    }
  });
</script>

<style lang="scss">
  @use '@squareup/public-web-design-system/dist/tokens/generated-tokens/scss/_media_queries' as mediaQueries;
  @use '@squareup/public-web-design-system/dist/styles/xms-fe/variables.scss' as vars;

  @layer custom-components {
    :global(.expanding) {
      --scrollPercent: 0;
      --expandDifference: calc(var(--body-width-max) - var(--container-width-current));
      --expandAmount: calc(var(--expandDifference) * (var(--scrollPercent) / 100));

      border-radius: var(--container-border-radius);
      width: calc(var(--container-width-current) + var(--expandAmount));

      @media (max-width: vars.$body-width-max) {
        --expandDifference: calc(100vw - var(--container-width-current));
        border-radius: calc(var(--container-border-radius) * (1 - (var(--scrollPercent) / 100)));
      }

      @include mediaQueries.until('extra-small') {
        --expandDifference: calc(100% - var(--container-width-current));
      }
    }
  }
</style>
